/**
 * Typed platform-service failures.
 *
 * Fail-closed is the whole point (PLAN hazard F2): when a runtime has no real
 * implementation for a service, the service must *throw one of these*, never
 * silently fall through to a Capacitor web implementation — which on Electron
 * would mean mock printers, plaintext "secure" storage, and a register that
 * believes it printed a receipt it never printed.
 *
 * One file, three errors on purpose: they are a single closed vocabulary and
 * splitting them across files would make the set harder to read, not easier.
 */
/* eslint-disable max-classes-per-file */

/** A capability this runtime deliberately does not have. */
export class PlatformCapabilityUnsupportedError extends Error {
  readonly code = 'PLATFORM_CAPABILITY_UNSUPPORTED';

  constructor(
    readonly capability: string,
    readonly runtimeKind: string,
    message?: string
  ) {
    super(message ?? `'${capability}' is not supported on the '${runtimeKind}' runtime.`);
    this.name = 'PlatformCapabilityUnsupportedError';
  }
}

/**
 * The Electron preload bridge (`window.pointcraftElectron`) is absent or a
 * different major, or the service it backs has not been implemented yet.
 *
 * Phase 1 waves T3.A/T3.C/T3.D replace the throwing stubs one at a time; until
 * then this error is what a desktop caller sees, and it is deliberately loud.
 */
export class ElectronBridgeUnavailableError extends Error {
  readonly code = 'ELECTRON_BRIDGE_UNAVAILABLE';

  constructor(
    readonly surface: string,
    message?: string
  ) {
    super(
      message ??
        `The Electron bridge surface '${surface}' is unavailable. ` +
          'Refusing to fall back to a browser implementation.'
    );
    this.name = 'ElectronBridgeUnavailableError';
  }
}

/** `Capacitor.getPlatform()` returned a value PointCraft has no row for. */
export class UnknownRuntimeError extends Error {
  readonly code = 'UNKNOWN_RUNTIME';

  constructor(readonly detected: string) {
    super(
      `Unknown PointCraft runtime '${detected}'. Every runtime needs an explicit row in ` +
        'src/platform/capabilities.ts before the app can boot on it.'
    );
    this.name = 'UnknownRuntimeError';
  }
}
